import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useState } from 'react';

import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';
import Tooltip from 'shopper/components/Tooltip';

import useAliasWishlist from 'hooks/useAliasWishlist';
import useModal from 'hooks/useModal';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';

import { useUser } from 'providers/UserProvider';

import Context from './Context';
import Like from './LikeButton';
import Share from './Share';

import OFFER from 'constants/offer';

const WishlistSuggestionsModal = dynamic(
  () => import('./WishlistSuggestionsModal')
);

/**
 * Offers with status WAITING are only accessible by the user
 * who posted it, shouldn't be able to share. FINISHED offers
 * shouldn't be shared for some reason too.
 */
const isOfferShareableInSocialMedia = (offerStatusName) =>
  offerStatusName !== OFFER.STATUSES.WAITING &&
  offerStatusName !== OFFER.STATUSES.FINISHED;

const Actions = ({
  aliasId,
  gaEventCategory,
  keywords,
  offerId,
  offerLikes,
  offerPrice,
  offerPriceType,
  offerStatusName,
  offerTitle,
  offerCoupon = null,
  offerInstructions,
  userId,
}) => {
  const { showModal } = useModal();
  const { runCallbackIfLoggedIn } = useUser();
  const [
    hasSuggestedKeywordAddedInWishlist,
    setHasSuggestedKeywordAddedInWishlist,
  ] = useState(false);
  const { addOfferWishlistAlias, removeOfferWishlistAlias, aliasWishlist } =
    useAliasWishlist(gaEventCategory, {
      gaEventLabel: 'offer_details',
    });

  const isAliasInWishlist = aliasWishlist?.items.some(
    (item) => item.aliasId === aliasId
  );

  const isIconFilled = aliasId
    ? isAliasInWishlist
    : hasSuggestedKeywordAddedInWishlist;

  const onWishlistHandle = () => {
    if (aliasId) {
      runCallbackIfLoggedIn(() => {
        if (isAliasInWishlist) {
          removeOfferWishlistAlias({ aliasId });
          return;
        }

        addOfferWishlistAlias({ aliasId });
      });

      return;
    }

    sendEvent({
      action: 'show_wishlist_suggestions',
      category: gaEventCategory,
    });

    showModal(WishlistSuggestionsModal, {
      keywords,
      onAddKeyword: () => {
        setHasSuggestedKeywordAddedInWishlist(true);
      },
      onRemoveKeyword: () => {
        setHasSuggestedKeywordAddedInWishlist(false);
      },
    });
  };

  const getWishlistTooltipMessage = () => {
    if (aliasId) {
      return isIconFilled
        ? placeholder('actions.removeWishlistProductAlert')
        : placeholder('actions.receiveAlerts');
    }

    return isIconFilled
      ? placeholder('actions.removeWishlistOfferAlert')
      : placeholder('actions.receiveAlertsOffers');
  };

  return (
    <div className="flex">
      <Tooltip.Root className="relative">
        <Tooltip.Trigger>
          <Button
            className="ml-auto [&>svg]:text-rd-primary-700 dark:[&>svg]:text-rd-primary-700 dark:[&>svg]:hover:text-primary-400"
            icon={<Icon name={isIconFilled ? 'heart-filled' : 'heart'} />}
            size="size3"
            type="primary-ghost"
            onClick={onWishlistHandle}
          />
        </Tooltip.Trigger>
        <Tooltip.Box
          align="center"
          className="w-56 rounded-4 bg-rd-primary-100 px-8 text-center [&>span]:text-neutral-low-300"
          direction="top"
          size="size3"
        >
          {getWishlistTooltipMessage()}
          <div className="absolute bottom-[-10px] left-1/2 size-0 -translate-x-1/2 border-[6px] border-b-0 border-t-[12px] border-solid border-transparent border-t-rd-primary-100 dark:border-t-primary-300" />
        </Tooltip.Box>
      </Tooltip.Root>
      <div className="flex items-center justify-end gap-3">
        {isOfferShareableInSocialMedia(offerStatusName) && (
          <Share
            gaEventCategory={gaEventCategory}
            offerPrice={offerPrice}
            offerPriceType={offerPriceType}
            offerTitle={offerTitle}
          />
        )}
      </div>
      <Like
        gaEventCategory={gaEventCategory}
        likesCount={offerLikes}
        offerId={offerId}
        size="size3"
        type="primary"
        ghost
      />
      <Context
        gaEventCategory={gaEventCategory}
        offerCoupon={offerCoupon}
        offerId={offerId}
        offerInstructions={offerInstructions}
        offerPrice={offerPrice}
        offerPriceType={offerPriceType}
        offerStatusName={offerStatusName}
        userId={userId}
      />
    </div>
  );
};

Actions.propTypes = {
  aliasId: PropTypes.number,
  gaEventCategory: PropTypes.string.isRequired,
  keywords: PropTypes.arrayOf(PropTypes.string),
  offerCoupon: PropTypes.string,
  offerId: PropTypes.number.isRequired,
  offerInstructions: PropTypes.string.isRequired,
  offerLikes: PropTypes.number.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerTitle: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
};

export default Actions;
